import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelCard } from '../../shared/PanelCard/PanelCard';
import { StatBlock } from './StatBlock';
import classes from './LocationDistribution.module.css';
import type { ILocationShare } from '../types';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

type SortBy = 'location' | 'time';

interface ILocationDistributionProps {
  activeLocations: number;
  totalHours: number;
  /** Catalog-ordered list of floor rows (page applies `sortFloorsAscending`). */
  rows: ILocationShare[];
}

/**
 * Right-side panel with the per-floor percentage breakdown. Header surfaces
 * the active-location count + total hours; subheader hosts the "Percentage
 * Spent on Location" heading and a sort selector that mirrors trusstor's
 * `TagsLocationDistributionGraphSortByEnum`:
 *
 *   - **By Location** (default): rows render in catalog order — basements
 *     ascending, then floors ascending, then preparation area / others.
 *   - **By Time**: same rows resorted by descending percent so the
 *     busiest floors land at the top.
 */
export function LocationDistribution({ activeLocations, totalHours, rows }: ILocationDistributionProps) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortBy>('location');

  const { sortedRows, maxPercent } = useMemo(() => {
    const next = sortBy === 'location' ? rows : [...rows].sort((a, b) => b.percent - a.percent);
    const max = next.reduce((acc, row) => Math.max(acc, row.percent), 1);
    return { sortedRows: next, maxPercent: max };
  }, [rows, sortBy]);

  return (
    <PanelCard aria-label={t('reports.contractorAnalysis.locations.title')} className={classes.panelPinned}>
      <div className={classes.header}>
        <h3 className={classes.title}>
          {t('reports.contractorAnalysis.locations.title')}
        </h3>
      </div>

      <div className={classes.headerStats}>
        <StatBlock
          icon="pin"
          label={t('reports.contractorAnalysis.locations.activeLocations')}
          value={activeLocations}
        />
        <StatBlock
          icon="clock"
          label={t('reports.contractorAnalysis.locations.totalHours')}
          value={Math.round(totalHours)}
        />
      </div>

      <div className={classes.subheader}>
        <span>{t('reports.contractorAnalysis.locations.percentageHeading')}</span>
        <select
          className={classes.groupSelect}
          value={sortBy}
          onChange={(event) => {
            const next = event.target.value;
            if (next === 'location' || next === 'time') setSortBy(next);
          }}
          aria-label={t('reports.contractorAnalysis.locations.percentageHeading')}
        >
          <option value="location">{t('reports.contractorAnalysis.locations.sortBy.location')}</option>
          <option value="time">{t('reports.contractorAnalysis.locations.sortBy.time')}</option>
        </select>
      </div>

      <ScrollArea className={classes.list} viewportClassName={classes.listBody}>
        {sortedRows.map((row) => (
          <div className={classes.row} key={row.id}>
            <span className={classes.rowLabel}>{row.label}</span>
            <span className={classes.rowBarTrack} aria-hidden="true">
              <span
                className={classes.rowBarFill}
                style={{ width: `${(row.percent / maxPercent) * 100}%` }}
              />
            </span>
            <span className={classes.rowPercent}>{row.percent.toFixed(1)}%</span>
          </div>
        ))}
      </ScrollArea>
    </PanelCard>
  );
}
